import { createSelector } from 'reselect'
import { get, map, filter, last, sumBy, nth, findIndex } from 'lodash'

import {
  calculateTax,
  calculateServiceCharge,
  calculateSubtotal,
  calculateServiceFeeTaxRules,
  calculateIndividualTaxAmount,
} from '../utils/totalUtils'

import { getItemsForCurrentMenu, getMenuServiceCharge } from './items'
import { getCurrentMenuCategories } from './categories'
import { getCurrentMenu, getCurrentMenuStand, getUseNormalTaxRounding, getUseTaxClasses, makeGetCurrentMenu } from './menus'
import { getIsOrderTaxExempt, getTaxClasses, getTaxRules } from '../VNTaxes/Selectors'

export const getCurrentCartItem = (state, id) => last(filter(getCartItems(state), ['id', id]))

const getCartItemsStringified = (state) => {
  const items = get(getItemsForCurrentMenu(state), 'byId', {})
  const cartItems = getCartLineItems(state)
  const categories = get(getCurrentMenuCategories(state), 'byId', {})

  const formattedCartItems = map(cartItems, (cartItem) => {
    const item = items[cartItem.itemId]

    return {
      ...item,
      ...cartItem,
      color: get(categories, `${get(item, 'itemCategoryUuid')}.color`, null),
      modifiers: map(cartItem.modifiers, (modifierItemId) => items[modifierItemId?.split('|')[0]]),
    }
  })

  return JSON.stringify(formattedCartItems)
}

export const getCartItems = createSelector(getCartItemsStringified, (cartItems) => JSON.parse(cartItems))

export const getSignatureDefaults = (state) => {
  const selectedTipIndex = get(state, 'cart.selectedTipIndex', null)
  const selectedTipAmount = get(state, 'cart.selectedTipAmount', null)
  const shouldShowCharityFirst = get(state, 'cart.shouldShowCharityFirst', false)
  const signature = get(state, 'cart.signature', null)

  return {
    selectedTipIndex,
    selectedTipAmount,
    shouldShowCharityFirst,
    signature,
  }
}

/**
 * This selector will return all items for the order in progress in a format
 * that allow totals calculations to be performed
 *
 * @param {*} state
 * @returns all items from the current order in a format that allows totals calculation
 */
export const getCartItemsFromOrder = (state, orderId) => {
  const items = getItemsForCurrentMenu(state).byId ?? {}
  const cartItems = state.order?.byId?.[orderId]?.itemModels
  const categories = getCurrentMenuCategories(state)?.byId ?? {}

  const formattedCartItems = map(cartItems, (cartItem) => {
    const item = items[cartItem.id]

    return {
      ...item,
      ...cartItem,
      color: get(categories, `${get(item, 'itemCategoryUuid')}.color`, null),
      modifiers: map(cartItem.modifiers, (modifierItem) => items[modifierItem.id]),
    }
  })

  return formattedCartItems
}

export const makeGetCartItemByIndex = () => {
  return createSelector(
    getCartItems,
    (state, index) => index,
    (cartItems, index) => nth(cartItems, index),
  )
}

export const hasCartItems2 = (state) => get(state, 'cart.lineItems.length', 0) > 0

export const getTaxRate = createSelector(makeGetCurrentMenu(), (currentMenu) => {
  return currentMenu.taxRate ?? 0
})

// TODO: Implement this when we know where to fetch the tax strategy metadata
export const getTaxByItem = createSelector(makeGetCurrentMenu(), (currentMenu) => {
  return currentMenu.taxByItem ?? true
})

export const getSubtotal = createSelector(getCartItems, (cartItems) => {
  return calculateSubtotal(cartItems)
})

export const getServiceCharge = (state) => {
  const subtotal = getSubtotal(state)
  const menuServiceCharge = getMenuServiceCharge(state)

  return calculateServiceCharge(menuServiceCharge, subtotal)
}

export const getServiceChargeTax = (state, skipExemptions = false) => {
  const taxRate = getTaxRate(state)
  const serviceCharge = getMenuServiceCharge(state)
  const useNormalTaxRounding = getUseNormalTaxRounding(state)
  const taxClasses = getTaxClasses(state)
  const taxRules = getTaxRules(state)
  const useTaxClasses = getUseTaxClasses(state)
  const taxExempt = getIsOrderTaxExempt(state)

  const serviceFee = {
    name: serviceCharge.name,
    total: getServiceCharge(state),
    legal: serviceCharge.legal,
    taxClassUuid: serviceCharge?.taxClassUuid,
  }

  if (useTaxClasses) {
    const result = calculateServiceFeeTaxRules(
      {},
      serviceFee,
      useNormalTaxRounding,
      taxClasses,
      taxRules,
      skipExemptions ? false : taxExempt,
    )

    return result?.serviceFeeTaxAmountInCents || 0
  }

  return calculateIndividualTaxAmount(taxRate, serviceFee.total, serviceCharge.taxable, useNormalTaxRounding)
}

export const getTax = createSelector(
  [
    getCartItems,
    getCurrentMenu,
    getCurrentMenuStand,
    getTaxClasses,
    getTaxRules,
    (state, skipExemptions = false) => getServiceChargeTax(state, skipExemptions),
    getIsOrderTaxExempt,
    (_state, skipExemptions = false) => skipExemptions,
  ],
  (cartItems, menu, stand, taxClasses, taxRules, serviceChargeTax, taxExempt, skipExemptions) => {
    const taxRate = menu.taxRate || 0
    const taxByItem = menu.taxByItem ?? true
    const useNormalTaxRounding = stand?.useNormalTaxRounding ?? false
    const useTaxClasses = stand?.useTaxClasses ?? false
    const calculateTaxResult = calculateTax(
      cartItems,
      taxRate,
      taxByItem,
      useNormalTaxRounding,
      useTaxClasses,
      taxClasses,
      taxRules,
      skipExemptions ? false : taxExempt,
    )

    return calculateTaxResult.taxAmountInCents + serviceChargeTax
  },
)

export const getTotal = createSelector(
  getSubtotal,
  getServiceCharge,
  getTax,
  (subtotal, serviceCharge, tax) => subtotal + serviceCharge + tax,
)

export const getCartQuantity2 = createSelector(getCartItems, (cartItems) => sumBy(cartItems, 'quantity'))

export const alcoholItemCountSelector = createSelector(getCartItems, (items) =>
  items.reduce((total, item) => total + (item.isAlcohol ? item.quantity : 0), 0),
)

export const getCartIndexByItemId = (store, itemId) =>
  findIndex(getCartLineItems(store), (lineItem) => lineItem.itemId === itemId)

export const getCartLineItems = (store) => get(store, 'cart.lineItems', [])

export const hasOpenPriceItem = createSelector(getCartLineItems, (lineItems) =>
  lineItems.some((lineItem) => lineItem.openName?.length),
)

export const cartHasPriceOverrideItem = createSelector(getCartLineItems, (lineItems) =>
  lineItems.some((lineItem) => lineItem?.openPriceInCents && lineItem?.openName === null),
)

export const hasCartItems = (state) => get(state, 'cart.lineItems.length', 0) > 0

/**
 * @deprecated - cart scrolls item to view on quantity changes no need to set item focus in reducer
 */
export const getNewItemFocus = (state) => state?.cart?.newItemFocus ?? false

export const getCartItemsCount = (state) => state?.cart?.lineItems?.length ?? 0

export const getCartItemsCountWithQuantities = (state) => {
  return state?.cart?.lineItems?.reduce((total, item) => total + item.quantity, 0) ?? 0
}

export const getCartNotes = (state) => state?.cart?.userNotes ?? null

export const getOrderNotes = (state) => state?.cart?.orderNotes ?? null

const getTaxExemptDataStringified = (state) =>
  JSON.stringify(state?.cart?.taxExemptData ?? { reason: '', taxExempt: false, taxExemptApproverUuid: '' })

export const getTaxExemptData = createSelector(getTaxExemptDataStringified, (taxExemptData) => {
  return JSON.parse(taxExemptData)
})

/**
 * Determines if the default cart index should be used for a menu item
 *
 * @param {Object} cartItem
 * @returns {boolean}
 */
const hasNotesOrModifiers = (cartItem) => {
  const hasPriceAdjustment = cartItem.openPriceInCents !== null && cartItem.openPriceInCents !== undefined

  return cartItem.notes?.length || cartItem.modifiers?.length || hasPriceAdjustment
}

/**
 * Get the total quantity of the item in the cart that have no modifiers, notes or price adjustments
 *
 * @param {Object} state
 * @param {String} itemId
 * @returns {Number} quantity of the cart item
 */
export const getItemQuantity = createSelector([getCartLineItems, (_, itemId) => itemId], (cartLineItems, itemId) => {
  let qty = 0
  cartLineItems.forEach((cartItem) => {
    if (cartItem.itemId !== itemId) return
    if (hasNotesOrModifiers(cartItem)) return

    qty += cartItem.quantity
  })
  return qty
})

/**
 * Get the quantity of the cart item that matches the itemId including items with notes/modifiers/price adjustments
 * @param {Object} state
 * @param {String} itemId
 * @returns {Number} quantity of the cart item
 */
export const getItemModifiedQuantity = createSelector([getCartLineItems, (_, itemId) => itemId], (cartLineItems, itemId) => {
  let qty = 0
  cartLineItems.forEach((cartItem) => {
    if (cartItem.itemId !== itemId) return
    if (!hasNotesOrModifiers(cartItem)) return

    qty += cartItem.quantity
  })
  return qty
})

/**
 * Get the index of the cart item that matches the itemId
 * returns -1 if no match is found or if the item has notes/modifiers/price adjustments
 *
 * @param {Object} state
 * @param {String} itemId
 * @returns {Number} index of the cart item or -1 if not found
 */
export const getCartItemIndex = createSelector([getCartLineItems, (_, itemId) => itemId], (cartLineItems, itemId) =>
  findIndex(cartLineItems, (cartItem) => {
    if (cartItem.itemId !== itemId) return false
    if (hasNotesOrModifiers(cartItem)) return false
    return true
  }),
)
